import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Dimensions, Modal, Pressable, ScrollView, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { type VisitRequestListItem } from "@/lib/api";
import { colors, fonts, radius, requestStatusBadge, spacing, typography } from "@/lib/theme";

const SCREEN_HEIGHT = Dimensions.get("window").height;

/** "YYYY-MM-DD" → "Mon, Jun 22" in the device's local calendar. The date is a
 *  plain calendar day (no tz), so build it from the parts to avoid a UTC shift. */
function formatProposedDate(isoDate: string): string {
    const [y, m, d] = isoDate.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric"
    });
}

/** "HH:MM" (24h) → "9:30 AM". */
function formatProposedTime(hhmm: string): string {
    const [h, m] = hhmm.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

/**
 * The screen to open for a request, or null when the visit isn't the nurse's to
 * view. A pending request keeps the visit on hold for her (→ the available-visit
 * detail, where the existing pending/cancel UI lives); an accepted request that
 * is assigned to her is now a scheduled visit (→ the visit screen). Rejected /
 * cancelled requests — or an accepted one that was reassigned — belong to
 * another nurse or are re-broadcast, so there's nothing of hers to open.
 */
export function requestVisitRoute(r: VisitRequestListItem): string | null {
    if (r.status === "pending") {
        return `/available-visit/${r.visitId}`;
    }
    if (r.status === "accepted" && r.assignedToMe) {
        return `/visit/${r.visitId}`;
    }
    return null;
}

function RequestRow({ request, onPress }: { request: VisitRequestListItem; onPress?: () => void }) {
    const badge = requestStatusBadge[request.status] ?? requestStatusBadge.pending;
    const clickable = onPress != null;
    const Container: typeof Pressable | typeof View = clickable ? Pressable : View;
    return (
        <Container
            style={
                clickable
                    ? ({ pressed }: { pressed: boolean }) => [
                          styles.row,
                          pressed && styles.rowPressed
                      ]
                    : styles.row
            }
            onPress={onPress}
        >
            <View style={styles.topRow}>
                <View style={styles.nameWrap}>
                    <View style={[styles.dot, { backgroundColor: badge.accent }]} />
                    <Text style={styles.patientName} numberOfLines={1}>
                        {request.patientName}
                    </Text>
                </View>
                <View style={[styles.badge, { backgroundColor: badge.background }]}>
                    <Text style={[styles.badgeText, { color: badge.accent }]}>{badge.label}</Text>
                </View>
            </View>

            <Text style={styles.meta} numberOfLines={1}>
                {request.visitType ?? "Visit"} &middot; ${request.payment}
            </Text>

            <View style={styles.dateRow}>
                <View style={styles.dateWrap}>
                    <MaterialCommunityIcons
                        name="calendar-clock"
                        size={14}
                        color={colors.textSecondary}
                    />
                    <Text style={styles.dateText}>
                        {formatProposedDate(request.proposedDate)} &middot;{" "}
                        {formatProposedTime(request.proposedTime)}
                    </Text>
                </View>
                {clickable && (
                    <MaterialCommunityIcons
                        name="chevron-right"
                        size={18}
                        color={colors.textTertiary}
                    />
                )}
            </View>
        </Container>
    );
}

/**
 * Bottom-sheet panel listing the nurse's own visit requests — pending offers
 * plus recently approved / cancelled / rejected ones. Opened from the Explore
 * tab's "My Requests" button. Rows whose visit is still hers (pending, or
 * approved-and-assigned) are tappable and call `onSelect`; the rest are
 * read-only. Cancellation itself stays the support-ticket flow on the visit
 * detail screen.
 */
export function MyRequestsPanel({
    visible,
    requests,
    onClose,
    onSelect
}: {
    visible: boolean;
    requests: VisitRequestListItem[];
    onClose: () => void;
    onSelect: (request: VisitRequestListItem) => void;
}) {
    return (
        <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
            <Pressable style={styles.backdrop} onPress={onClose} />
            <View style={styles.sheet}>
                <SafeAreaView edges={["bottom"]} style={styles.safeArea}>
                    <View style={styles.handleArea}>
                        <View style={styles.handle} />
                    </View>
                    <View style={styles.header}>
                        <View style={styles.headerRow}>
                            <Text style={styles.title}>My Requests</Text>
                            <Pressable onPress={onClose} hitSlop={12}>
                                <MaterialCommunityIcons
                                    name="close"
                                    size={22}
                                    color={colors.textSecondary}
                                />
                            </Pressable>
                        </View>
                        <Text style={styles.subtitle}>
                            Visits you asked to take and how each turned out.
                        </Text>
                    </View>

                    {requests.length === 0 ? (
                        <View style={styles.empty}>
                            <MaterialCommunityIcons
                                name="clipboard-text-clock-outline"
                                size={40}
                                color={colors.textTertiary}
                            />
                            <Text style={styles.emptyText}>
                                You have no recent requests right now.
                            </Text>
                        </View>
                    ) : (
                        <ScrollView
                            style={styles.list}
                            contentContainerStyle={styles.listContent}
                            showsVerticalScrollIndicator={false}
                        >
                            {requests.map((r) => {
                                const clickable = requestVisitRoute(r) != null;
                                return (
                                    <RequestRow
                                        key={r.requestId}
                                        request={r}
                                        onPress={clickable ? () => onSelect(r) : undefined}
                                    />
                                );
                            })}
                        </ScrollView>
                    )}
                </SafeAreaView>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    backdrop: {
        ...StyleSheet.absoluteFill,
        backgroundColor: "rgba(0, 0, 0, 0.4)"
    },
    sheet: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        maxHeight: SCREEN_HEIGHT * 0.8,
        backgroundColor: colors.surface,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        overflow: "hidden"
    },
    // The sheet is height-capped by maxHeight; the SafeAreaView must be allowed
    // to shrink within it (flexShrink) so the ScrollView below gets a bounded
    // frame and can actually scroll instead of growing to its content height.
    safeArea: {
        flexShrink: 1
    },
    handleArea: {
        paddingTop: 12,
        paddingBottom: 4,
        alignItems: "center"
    },
    handle: {
        width: 36,
        height: 5,
        borderRadius: 3,
        backgroundColor: colors.gray300
    },
    header: {
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.sm,
        paddingBottom: spacing.md
    },
    headerRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },
    title: {
        ...typography.h2,
        color: colors.textPrimary
    },
    subtitle: {
        ...typography.caption,
        color: colors.textSecondary,
        marginTop: spacing.xs
    },
    list: {
        flexShrink: 1,
        paddingHorizontal: spacing.lg
    },
    listContent: {
        paddingBottom: spacing.lg,
        gap: spacing.sm
    },
    row: {
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.base,
        backgroundColor: colors.background,
        borderRadius: radius.md,
        gap: 6
    },
    rowPressed: {
        opacity: 0.6
    },
    topRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: spacing.sm
    },
    nameWrap: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm
    },
    dot: {
        width: 10,
        height: 10,
        borderRadius: 5
    },
    patientName: {
        ...typography.subtitle,
        color: colors.textPrimary,
        flexShrink: 1
    },
    meta: {
        ...typography.caption,
        color: colors.textSecondary,
        marginLeft: 18
    },
    dateRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginLeft: 18
    },
    dateWrap: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6
    },
    dateText: {
        ...typography.bodySmall,
        color: colors.textPrimary
    },
    badge: {
        paddingHorizontal: spacing.sm,
        paddingVertical: 4,
        borderRadius: radius.full
    },
    badgeText: {
        fontSize: 11,
        fontFamily: fonts.bold
    },
    empty: {
        alignItems: "center",
        gap: spacing.sm,
        paddingVertical: spacing["3xl"],
        paddingHorizontal: spacing.lg
    },
    emptyText: {
        ...typography.body,
        color: colors.textSecondary,
        textAlign: "center"
    }
});
